import { useEffect, useState } from 'react';
import { Modal } from '../components/parts';
import { CardCodex } from '../components/CardCodex';
import { CoinIcon, RoleIcon } from '../components/Icon';
import { fetchLeaderboard, fetchWeeklyLeaderboard } from '../game/leaderboard';
import {
  DAILY_COOLDOWN_MS,
  DAILY_GRACE_MS,
  DAILY_LADDER,
  dailyReward,
  fetchWallet,
} from '../game/shop';
import { claimDailyBonus } from '../game/api';
import { track } from '../analytics';
import { ShopModal } from '../components/Shop';
import { flagEmoji } from '../game/locale';
import { usePublicRooms } from '../game/usePublicRooms';
import type { LeaderboardEntry } from '../game/api';
import type { GameSession } from '../game/useGameSession';
import {
  cardSuit,
  characterAbilityKey,
  characterHp,
  characterName,
  characterRoster,
  roleColor,
} from '../game/cardMeta';
import { cardArt, characterArt, roleArt } from '../game/art';
import { useT } from '../i18n';
import type { CardKind, Role } from '../game/types';

export function Home({
  session,
  handle,
  photoURL,
  ready,
  onOpenSettings,
  onOpenAdmin,
}: {
  session: GameSession;
  handle: string;
  /** The player's avatar photo (Google), shown in the shop's frame previews. */
  photoURL: string | null;
  /** False until the anonymous/real uid lands — create/join need it, the rest of the page doesn't. */
  ready: boolean;
  onOpenSettings: () => void;
  onOpenAdmin?: () => void;
}) {
  const t = useT();
  const [showJoin, setShowJoin] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showCodex, setShowCodex] = useState(false);
  const [showShop, setShowShop] = useState(false);

  // Home is the first (and, pre-login, only) screen every visitor and search
  // crawler sees, so its FAQ copy carries matching FAQPage structured data.
  useEffect(() => {
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.home.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [t]);

  return (
    <div className="screen home-screen">
      <div className="home-hero">
        {/* Brand / pitch */}
        <div className="home-brand">
          <h1 className="title">NEONFALL</h1>
          <div className="tagline">{t.home.tagline}</div>
          <ul className="home-features">
            {t.home.features.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>

        {/* Action panel */}
        <div className="home-actions">
          <div className="home-cta">
            <button
              className="btn block lime"
              disabled={!ready || session.busy}
              onClick={() => session.quickPlay()}
            >
              ⚡ {t.home.quickPlay}
            </button>
            <div className="mono-dim quickplay-hint">{t.home.quickPlayHint}</div>
            <button
              className="btn block magenta"
              disabled={!ready || session.busy}
              onClick={() => session.createRoom()}
            >
              {t.home.create}
            </button>
            <button
              className="btn block cyan"
              disabled={!ready}
              onClick={() => setShowJoin(true)}
            >
              {t.home.join}
            </button>
            <button
              className="btn block violet"
              disabled={!ready}
              onClick={() => setShowBrowse(true)}
            >
              {t.home.publicGames}
            </button>
          </div>

          <div className="home-tiles">
            <button className="home-tile" onClick={() => setShowHowTo(true)}>
              <span className="home-tile-ico">?</span>
              <span className="home-tile-label">{t.home.howto}</span>
            </button>
            <button className="home-tile" onClick={() => setShowCodex(true)}>
              <span className="home-tile-ico">▤</span>
              <span className="home-tile-label">{t.home.codex}</span>
            </button>
            <button className="home-tile" disabled={!ready} onClick={() => setShowShop(true)}>
              <span className="home-tile-ico"><CoinIcon size={22} /></span>
              <span className="home-tile-label">{t.home.shop}</span>
            </button>
            {/* Profile and Settings live in one modal (tabs) — one tile for both. */}
            <button className="home-tile" onClick={onOpenSettings}>
              <span className="home-tile-ico">◈</span>
              <span className="home-tile-label">{t.home.profile}</span>
            </button>
          </div>

          {onOpenAdmin && (
            <button className="home-tile-admin" onClick={onOpenAdmin}>
              <span className="home-tile-admin-ico">★</span>
              <span className="home-tile-admin-label">Admin</span>
            </button>
          )}

          {ready && session.myUid && <DailyBonus uid={session.myUid} />}

          <Leaderboard />

          <div className="home-foot mono-dim">
            {t.home.secure} · {handle}
          </div>
        </div>
      </div>

      <div className="home-seo">
        <section className="home-seo-block">
          <h2>{t.home.seoHeading1}</h2>
          <p>{t.home.seoBody1}</p>
        </section>
        <section className="home-seo-block">
          <h2>{t.home.seoHeading2}</h2>
          <p>{t.home.seoBody2}</p>
        </section>
        <section className="home-seo-block">
          <h2>{t.home.seoHeading3}</h2>
          <p>{t.home.seoBody3}</p>
        </section>
        <section className="home-seo-block home-faq">
          <h2>{t.home.faqHeading}</h2>
          <div className="home-faq-list">
            {t.home.faq.map((item, i) => (
              <div className="home-faq-item" key={i}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <footer className="home-footer mono-dim">
        <a className="home-contact" href="mailto:support@neon-fall.com">
          {t.home.contact}
        </a>
        <span className="home-protocol">{t.home.protocol}</span>
      </footer>

      {showJoin && (
        <JoinModal
          busy={session.busy}
          onClose={() => setShowJoin(false)}
          onJoin={(code) => {
            setShowJoin(false);
            session.joinRoom(code);
          }}
        />
      )}
      {showBrowse && (
        <PublicRoomsModal
          busy={session.busy}
          onClose={() => setShowBrowse(false)}
          onJoin={(code) => {
            setShowBrowse(false);
            session.joinRoom(code);
          }}
          onWatch={(roomId) => {
            setShowBrowse(false);
            session.spectate(roomId);
          }}
        />
      )}
      {showHowTo && <HowToModal onClose={() => setShowHowTo(false)} />}
      {showCodex && <CardCodex onClose={() => setShowCodex(false)} />}
      {showShop && session.myUid && (
        <ShopModal
          uid={session.myUid}
          name={handle}
          photoURL={photoURL}
          onClose={() => setShowShop(false)}
        />
      )}
    </div>
  );
}

/** "13:07:42" until the given epoch ms (floors at zero). */
function countdown(untilMs: number, nowMs: number): string {
  const left = Math.max(0, Math.ceil((untilMs - nowMs) / 1000));
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Daily login bonus: a personal 24h timer from the previous claim and a 7-step
 * ladder up to ⬡100 (server-enforced; this panel only renders the wallet's
 * dailyAt/dailyStreak and ticks the countdown).
 */
function DailyBonus({ uid }: { uid: string }) {
  const t = useT();
  // null = still loading; hide the panel until the wallet answers.
  const [wallet, setWallet] = useState<{ dailyAt: number; dailyStreak: number } | null>(null);
  const [justClaimed, setJustClaimed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    fetchWallet(uid)
      .then((w) => alive && setWallet({ dailyAt: w.dailyAt, dailyStreak: w.dailyStreak }))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [uid]);

  // Tick every second while the cooldown runs; the countdown flips the panel
  // back to claimable on its own when it reaches zero.
  const onCooldown = !!wallet && wallet.dailyAt > 0 && now < wallet.dailyAt + DAILY_COOLDOWN_MS;
  useEffect(() => {
    if (!onCooldown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [onCooldown]);

  if (!wallet) return null;

  // The ladder step the NEXT claim pays (or the one just collected while on
  // cooldown): streak survives if the claim lands within the 48h grace window.
  const streakAlive = wallet.dailyAt > 0 && now <= wallet.dailyAt + DAILY_GRACE_MS;
  const nextStreak = streakAlive ? wallet.dailyStreak + 1 : 1;
  const shownStreak = onCooldown ? wallet.dailyStreak : nextStreak;
  const shownDay = Math.min(Math.max(shownStreak, 1), DAILY_LADDER.length);

  async function claim() {
    if (busy || onCooldown) return;
    setBusy(true);
    try {
      const res = await claimDailyBonus();
      setWallet({ dailyAt: Date.now(), dailyStreak: res.streak });
      setJustClaimed(true);
      track('daily_bonus', { streak: res.streak, reward: res.reward });
    } catch {
      // Most likely claimed from another tab — re-read the truth.
      fetchWallet(uid)
        .then((w) => setWallet({ dailyAt: w.dailyAt, dailyStreak: w.dailyStreak }))
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={'daily-bonus' + (onCooldown ? ' claimed' : '')}>
      <div className="row between" style={{ gap: 12 }}>
        <div className="col" style={{ gap: 2, minWidth: 0 }}>
          <span className="daily-title">🎁 {t.home.daily.title}</span>
          <span className="mono-dim daily-sub">
            {shownStreak > 1 ? `🔥 ${t.home.daily.day(shownDay)} · ` : ''}
            {onCooldown
              ? t.home.daily.nextIn(countdown(wallet.dailyAt + DAILY_COOLDOWN_MS, now))
              : t.home.daily.hint}
          </span>
        </div>
        {onCooldown ? (
          <span className={'daily-claimed' + (justClaimed ? ' pop' : '')}>
            {justClaimed ? '+' : '✓ '}
            {justClaimed && dailyReward(wallet.dailyStreak)}
            <CoinIcon size={14} />
            {!justClaimed && dailyReward(wallet.dailyStreak)}
          </span>
        ) : (
          <button className="btn small amber daily-claim-btn" disabled={busy} onClick={claim}>
            {t.home.daily.claim} <CoinIcon size={13} /> {dailyReward(nextStreak)}
          </button>
        )}
      </div>
      {/* The 7-day ladder: collected days lit, the day in play highlighted. */}
      <div className="daily-ladder">
        {DAILY_LADDER.map((coins, i) => {
          const day = i + 1;
          const done = onCooldown ? day <= shownDay : day < shownDay;
          const active = day === shownDay;
          return (
            <span
              key={day}
              className={'daily-cell' + (done ? ' done' : '') + (active ? ' active' : '')}
              title={t.home.daily.day(day)}
            >
              <span className="daily-cell-day">{day}</span>
              <span className="daily-cell-coins"><CoinIcon size={10} />{coins}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function PublicRoomsModal({
  busy,
  onClose,
  onJoin,
  onWatch,
}: {
  busy: boolean;
  onClose: () => void;
  onJoin: (code: string) => void;
  onWatch: (roomId: string) => void;
}) {
  const t = useT();
  const rooms = usePublicRooms(true);
  const timerLabel = (s: number) => (s < 60 ? `${s}${t.home.sec}` : `${s / 60}${t.home.min}`);
  const lobbies = rooms.filter((r) => r.status !== 'playing');
  const live = rooms.filter((r) => r.status === 'playing');

  const row = (r: (typeof rooms)[number]) => {
    const full = r.playerCount >= r.maxPlayers;
    const isLive = r.status === 'playing';
    return (
      <div key={r.roomId} className="pubroom-row">
        <div className="col pubroom-info">
          <span className="pubroom-host">
            {isLive && <span className="live-dot">● {t.browse.live}</span>}
            {r.hostCountry && <span className="lb-flag">{flagEmoji(r.hostCountry)}</span>}
            <span className="lb-nametext">{r.hostName}</span>
          </span>
          <span className="mono-dim pubroom-meta">
            {r.playerCount}/{r.maxPlayers} {t.browse.runners} · {timerLabel(r.turnLimit)} ·{' '}
            {t.home.diff[r.botDifficulty] ?? r.botDifficulty}
          </span>
        </div>
        {isLive ? (
          <button className="btn small violet" disabled={busy} onClick={() => onWatch(r.roomId)}>
            👁 {t.browse.watch}
          </button>
        ) : (
          <button className="btn small cyan" disabled={full || busy} onClick={() => onJoin(r.code)}>
            {full ? t.browse.full : t.browse.join}
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal onClose={onClose}>
      <div className="col" style={{ gap: 16 }}>
        <div className="row between">
          <div className="col" style={{ gap: 4 }}>
            <h2 className="h2">{t.browse.title}</h2>
            <div className="mono-dim">{t.browse.subtitle}</div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>

        {rooms.length === 0 ? (
          <div className="mono-dim pubroom-empty">{t.browse.empty}</div>
        ) : (
          <div className="col" style={{ gap: 14 }}>
            {live.length > 0 && (
              <div className="col" style={{ gap: 8 }}>
                <div className="eyebrow">{t.browse.liveGames}</div>
                <div className="pubroom-list">{live.map(row)}</div>
              </div>
            )}
            {lobbies.length > 0 && (
              <div className="col" style={{ gap: 8 }}>
                <div className="eyebrow">{t.browse.lobbies}</div>
                <div className="pubroom-list">{lobbies.map(row)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Leaderboard() {
  const t = useT();
  const [tab, setTab] = useState<'week' | 'all'>('week');
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);

  useEffect(() => {
    let alive = true;
    // Keep the previous list on tab switch — the rows swap in place without
    // the whole panel collapsing while the new query is in flight.
    (tab === 'week' ? fetchWeeklyLeaderboard(5) : fetchLeaderboard(5))
      .then((rows) => alive && setEntries(rows))
      .catch(() => alive && setEntries([]));
    return () => {
      alive = false;
    };
  }, [tab]);

  if (!entries) return null; // stay quiet until loaded (avoids a layout flash)

  return (
    <div className="leaderboard">
      <div className="eyebrow lb-title">🏆 {t.home.leaderboard}</div>
      <div className="lb-tabs">
        <button
          className={tab === 'week' ? 'lb-tab active' : 'lb-tab'}
          onClick={() => setTab('week')}
        >
          {t.home.lbWeek}
        </button>
        <button
          className={tab === 'all' ? 'lb-tab active' : 'lb-tab'}
          onClick={() => setTab('all')}
        >
          {t.home.lbAllTime}
        </button>
      </div>
      {entries.length === 0 ? (
        <div className="mono-dim lb-empty">{t.home.lbEmpty}</div>
      ) : (
        <ol className="lb-list">
          {entries.map((e, i) => (
            <li key={e.uid} className="lb-row">
              <span className="lb-rank">{i + 1}</span>
              <span className="lb-name">
                {e.country && <span className="lb-flag">{flagEmoji(e.country)}</span>}
                <span className="lb-nametext">{e.name}</span>
              </span>
              <span className="lb-mvp" title={t.home.lbMvp}>★ {e.mvp}</span>
              <span className="lb-games mono-dim">{e.games} {t.home.lbGames}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function JoinModal({
  busy,
  onClose,
  onJoin,
}: {
  busy: boolean;
  onClose: () => void;
  onJoin: (code: string) => void;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const valid = /^[A-Z0-9]{6}$/.test(code);
  return (
    <Modal onClose={onClose}>
      <div className="col" style={{ gap: 16 }}>
        <div className="col" style={{ gap: 4 }}>
          <h2 className="h2">{t.join.title}</h2>
          <div className="mono-dim">{t.join.subtitle}</div>
        </div>
        <input
          className="input code"
          autoFocus
          maxLength={6}
          placeholder={t.join.placeholder}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && valid && onJoin(code)}
        />
        <div className="row" style={{ gap: 10 }}>
          <button className="btn ghost block" onClick={onClose}>{t.join.cancel}</button>
          <button className="btn cyan block" disabled={!valid || busy} onClick={() => onJoin(code)}>
            {t.join.action}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const ROLE_ORDER: Role[] = ['director', 'security', 'rebel', 'fixer'];

// Card art illustrating each t.howto.sections entry (by index, so it is
// language-independent): reach → weapons, combat → attack/defence, rewards →
// the payout. null = text-only section.
const SECTION_CARDS: (CardKind[] | null)[] = [
  null, // The Goal
  null, // Your Turn
  ['rig', 'longwave'], // Network Proximity
  ['hack', 'firewall', 'cipherShield'], // Combat
  ['payday'], // Rewards
  null, // Winning
];

function HowToModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <Modal onClose={onClose}>
      <div className="col" style={{ gap: 20 }}>
        <div className="row between">
          <h2 className="h2">{t.howto.title}</h2>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>

        <p className="howto-intro">{t.howto.intro}</p>

        {/* Factions — role portraits with the faction icon badge */}
        <div className="col" style={{ gap: 10 }}>
          <div className="eyebrow">{t.howto.factionsHeader}</div>
          <div className="col" style={{ gap: 10 }}>
            {ROLE_ORDER.map((r) => (
              <div key={r} className="howto-role">
                <span
                  className="howto-role-art"
                  style={{ color: roleColor[r], backgroundImage: `url(${roleArt(r)})` }}
                >
                  <span className="howto-role-badge"><RoleIcon role={r} size={13} /></span>
                </span>
                <div className="col" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 800, color: roleColor[r] }}>{t.role.name[r]}</span>
                  <span className="mono-dim" style={{ fontSize: 12, lineHeight: 1.45 }}>
                    {t.role.desc[r]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mechanics — illustrated with the actual cards where it helps */}
        <div className="col" style={{ gap: 14 }}>
          {t.howto.sections.map((s, i) => {
            const kinds = SECTION_CARDS[i];
            return (
              <div key={i} className="col" style={{ gap: 4 }}>
                <div className="eyebrow">{s.h}</div>
                <div className="mono-dim" style={{ lineHeight: 1.5 }}>{s.b}</div>
                {kinds && (
                  <div className="howto-card-strip">
                    {kinds.map((k) => (
                      <span key={k} className="howto-card-cell" style={{ color: cardSuit[k].color }}>
                        <span
                          className="howto-card-thumb"
                          style={{ backgroundImage: `url(${cardArt(k)})` }}
                        />
                        <span className="howto-card-name">{t.card.name[k]}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Character roster — portrait + HP + superpower for each */}
        <div className="col" style={{ gap: 8 }}>
          <div className="eyebrow">{t.howto.charactersHeader}</div>
          <div className="mono-dim" style={{ fontSize: 12, lineHeight: 1.4 }}>{t.howto.charactersNote}</div>
          {characterRoster.map((id) => (
            <div key={id} className="row" style={{ gap: 10, alignItems: 'center' }}>
              <span
                className="howto-char-art"
                style={{ backgroundImage: `url(${characterArt(id)})` }}
              />
              <span className="char-hp">♥{characterHp[id]}</span>
              <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--violet)' }}>
                  {characterName[id]}
                </span>
                <span className="mono-dim" style={{ fontSize: 11.5, lineHeight: 1.4 }}>
                  {t.ability[characterAbilityKey[id] ?? 'none']}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Cards — point to the Codex instead of duplicating the full list */}
        <div className="col" style={{ gap: 4 }}>
          <div className="eyebrow">{t.howto.cardsHeader}</div>
          <div className="mono-dim" style={{ fontSize: 12, lineHeight: 1.4 }}>{t.howto.cardsNote}</div>
        </div>
      </div>
    </Modal>
  );
}
